import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../../lib/utils"
import { type ColorToken, colorTokenToVar } from "./color-tokens"
import { type SizeToken, sizeTokenClasses } from "./size-tokens"
import { type FontFamilyToken, fontFamilyClasses } from "./font-family-tokens"
import { type LineHeightToken, lineHeightClasses } from "./line-height-tokens"
import { type LetterSpacingToken, letterSpacingClasses } from "./letter-spacing-tokens"

const typographyVariants = cva("", {
  variants: {
    variant: {
      /* EPIC font-family.-brand (Poppins) for headings */
      h1: `font-brand text-3xl font-bold ${lineHeightClasses["2xl"]} tracking-tight`,
      h2: `font-brand text-2xl font-bold ${lineHeightClasses["xl"]}`,
      h3: `font-brand text-xl  font-bold ${lineHeightClasses["l"]}`,
      h4: `font-brand text-lg  font-bold ${lineHeightClasses["2m-heading"]}`,
      h5: "font-sans text-sm font-medium leading-normal",
      h6: "font-sans text-xs font-medium leading-normal",
      label: "font-sans text-xs leading-normal",
      overline: "font-sans text-2xs uppercase tracking-widest leading-normal",
      /* EPIC font-family.-sans (Geist) for body/labels */
      b1: `font-sans text-base ${lineHeightClasses["m-heading"]}`,
      b2: `font-sans text-sm  ${lineHeightClasses["s-heading"]}`,
      b3: `font-sans text-xs  ${lineHeightClasses["xs"]}`,
    },
  },
  defaultVariants: {
    variant: "b1",
  },
})

const weightClasses = {
  bold:     "font-bold",
  semibold: "font-semibold",
  medium:   "font-medium",
  regular:  "font-normal",
} as const

type TypographyVariant = NonNullable<VariantProps<typeof typographyVariants>["variant"]>
type TypographyWeight = keyof typeof weightClasses

const defaultElements: Record<TypographyVariant, React.ElementType> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  
  b1: "p",
  b2: "p",
  b3: "p",
  label: "span",
  overline: "span",
}

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant?: TypographyVariant
  weight?: TypographyWeight
  as?: React.ElementType
  color?: ColorToken
  /** Override font-size. Defaults to each variant's built-in size. */
  size?: SizeToken
  /** Override font-family: brand (Poppins) | sans (Geist) | mono (Geist Mono) */
  fontFamily?: FontFamilyToken
  /** Override line-height using EPIC design tokens */
  lineHeight?: LineHeightToken
  /** Override letter-spacing using EPIC design tokens */
  letterSpacing?: LetterSpacingToken
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  (
    {
      className,
      variant = "b1",
      weight,
      as,
      color,
      size,
      fontFamily,
      lineHeight,
      letterSpacing,
      style,
      ...props
    },
    ref
  ) => {
    const Component = as ?? defaultElements[variant]
    return (
      <Component
        ref={ref}
        className={cn(
          typographyVariants({ variant }),
          weight && weightClasses[weight],
          size && sizeTokenClasses[size],
          fontFamily && fontFamilyClasses[fontFamily],
          lineHeight && lineHeightClasses[lineHeight],
          letterSpacing && letterSpacingClasses[letterSpacing],
          className
        )}
        style={color ? { color: colorTokenToVar(color), ...style } : style}
        {...props}
      />
    )
  }
)
Typography.displayName = "Typography"

export { Typography, typographyVariants, weightClasses }
export type { TypographyVariant, TypographyWeight }
